import React from 'react';
import { useSelector } from 'react-redux';
import Image from '../../Utils/Image/image-component';
import SystemDataService from '../../Services/SystemDataService';
import NavLink from '../../Utils/NavLink/navlink-component';

/**
 * ServiceMaintenanceSection - Optimized Blueprint Edition
 * Refined proportions (40/60 split) with large-scale typography for readability.
 */
const ServiceMaintenanceSection = ({ data }) => {
    const systemData = useSelector((s) => s.systemData || {});
    const { dir = 'ltr', links = [] } = systemData;

    if (!data) return null;

    const { title, subtitle, tips, footerContent } = data;

    const phone = SystemDataService.byLabel(links, 'Phone');
    // const illustration = "https://sawpy-assets.s3.us-east-2.amazonaws.com/AlwaysOnAir/images/1770644036309-image.png";
    const illustration = "https://sawpy-assets.s3.us-east-2.amazonaws.com/tropicalmarine/gallery/1777551515731.jpg"

    return (
        <section dir={dir} className="w-full bg-white py-12 lg:py-24  md:px-6 font-assistant overflow-hidden">
            <div className="max-w-7xl mx-auto">

                {/* Adjusted Flex Proportions: 
                   lg:w-4/12 (Image) and lg:w-8/12 (Content) for a cleaner balance.
                */}
                <div className="flex flex-col lg:flex-row items-stretch gap-0 rounded-[2rem] lg:rounded-[3rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,51,102,0.2)] border border-primary-900/10">

                    {/* --- LEFT: COMPACT IMAGE SECTION (40%) --- */}
                    <div className="w-full lg:w-4/12 relative min-h-[400px] lg:min-h-full overflow-hidden">
                        <Image
                            src={illustration}
                            alt="Always On Air Specialist"
                            wrapperClassName="absolute inset-0 w-full h-full"
                            className="w-full h-full object-cover object-center"
                            useSekeleton={true}
                        />

                        <div className="absolute inset-0 bg-black/10 z-10 pointer-events-none"></div>
                    </div>

                    {/* --- RIGHT: HIGH-READABILITY TIPS AREA (60%) --- */}
                    <div className="w-full lg:w-8/12 bg-primary-900 p-8 sm:p-12 lg:p-20 relative overflow-hidden">
                        {/* Technical Grid Overlay */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] [background-size:30px_30px]"></div>

                        <div className="relative z-10">
                            <div className="mb-10 lg:mb-16">
                                <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white er leading-[0.9] uppercase mb-6">
                                    {title || "Maintenance Strategy"}
                                </h2>
                                {subtitle && (
                                    <p className="text-lg lg:text-2xl text-white/60 font-medium italic max-w-2xl leading-relaxed">
                                        “{subtitle}”
                                    </p>
                                )}
                            </div>

                            {/* Enhanced Typography for Tips */}
                            <div className="space-y-2">
                                {tips && tips.map((tip, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-6 lg:gap-10 py-6 lg:py-8 border-b border-white/10 group transition-all"
                                    >
                                        <div className="text-3xl lg:text-5xl font-black text-secondary-500 group-hover:scale-110 transition-transform flex-shrink-0">
                                            0{index + 1}
                                        </div>
                                        <div>
                                            <p className="text-lg md:text-xl lg:text-2xl text-white font-bold uppercase leading-tight group-hover:text-secondary-500 transition-colors">
                                                {typeof tip === 'object' ? tip.title : tip}
                                            </p>
                                            {typeof tip === 'object' && tip.description && (
                                                <p className="text-sm text-white/60 mt-1 font-normal normal-case">
                                                    {tip.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* --- ACTION FOOTER: Optimized for No-Wrap --- */}
                            {footerContent && (
                                <div className="mt-12 lg:mt-16 flex flex-col sm:flex-row items-center justify-between gap-8 pt-10 border-t border-white/10">

                                    {/* Text Container: flex-1 allows this to shrink if needed to protect the phone number */}
                                    <div className="flex-1 text-white/40 text-xs lg:text-sm font-medium uppercase tracking-widest text-center sm:text-left leading-relaxed">
                                        {footerContent.preLink}
                                        <NavLink
                                            href={footerContent.linkUrl}
                                            className="text-secondary-500 font-black mx-2 !opacity-100 border-b-2 border-secondary-500 inline-block"
                                        >
                                            {footerContent.linkText}
                                        </NavLink>
                                        {footerContent.postLink}
                                    </div>

                                    {/* Phone Container: whitespace-nowrap prevents the line break */}
                                    {phone?.url && (
                                        <div className="flex-shrink-0">
                                            <a
                                                href={phone.url}
                                                className="text-white font-black text-2xl lg:text-3xl hover:text-secondary-500 transition-colors flex items-center gap-4 whitespace-nowrap"
                                            >
                                                <svg className="w-6 h-6 lg:w-8 lg:h-8 text-secondary-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.26 1.12.32 2.33.49 3.57.49.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.17 2.45.49 3.57.1.35.01.74-.26 1.02l-2.2 2.2z" />
                                                </svg>
                                                {phone.description}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ServiceMaintenanceSection;